import React, { useState, useEffect, useCallback, useRef } from 'react';
import { discoveryActions } from '../../actions/discovery';
import ModelItem from '../../components/models/ModelItem';
import { colors, spacing, radius, typography, transitions, zIndex } from '../../styles/theme';
import useResponsive from '../../utils/useResponsive';

/**
 * Normalize item to unified structure
 * Handles both models (model_id) and workflows (workflow_definition_id)
 */
const normalizeItem = (item) => {
  const isWorkflow = item.item_type === 'workflow';
  
  // For workflows, map to unified model_* schema for ModelItem component
  if (isWorkflow) {
    return {
      ...item,
      model_id: item.workflow_definition_id,
      model_name: item.name,
      model_description: item.description,
      model_video_url: item.workflow_video_url,
      model_cover_image_url: item.workflow_cover_image_url,
      model_cover_2_image_url: item.workflow_cover_2_image_url,
      model_thumbnail_image_url: null, // workflow_definition doesn't have thumbnail column
    };
  }
  
  // Models already have the correct schema
  return item;
};

export default function CreateModal({ isOpen, onClose, onSelectItem, initialCategory }) {
  const { isMobile } = useResponsive();
  const [selectedCategory, setSelectedCategory] = useState(initialCategory || 'all');
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const initialFetchDone = useRef(false);

  // Debounce search query (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // API search when debounced query changes
  useEffect(() => {
    if (!debouncedSearch) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    const performSearch = async () => {
      setSearching(true);
      try {
        const res = await discoveryActions.searchDiscovery(debouncedSearch, 'all', true);
        if (res.success && res.data?.results) {
          setSearchResults(res.data.results.map(normalizeItem));
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        console.error('Search failed:', err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    };

    performSearch();
  }, [debouncedSearch]);

  // Display items: search results when searching, otherwise category items
  const displayItems = debouncedSearch ? searchResults : items;
  const isSearchMode = !!debouncedSearch;

  // Update selectedCategory when initialCategory changes (for when modal reopens with different category)
  useEffect(() => {
    if (initialCategory) {
      setSelectedCategory(initialCategory);
    }
  }, [initialCategory]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      initialFetchDone.current = false;
      setSearchQuery('');
      setDebouncedSearch('');
      setSearchResults([]);
    }
  }, [isOpen]);

  // Fetch categories AND initial items in parallel on open
  useEffect(() => {
    if (!isOpen || initialFetchDone.current) return;
    initialFetchDone.current = true;

    const fetchInitialData = async () => {
      setLoading(true);
      
      try {
        // Fetch categories and initial items in PARALLEL
        const [categoriesRes, itemsRes] = await Promise.all([
          discoveryActions.getDiscoveryData({
            type: 'all',
            zenEnabled: true,
            trendingLimit: 1,
            categoryItemLimit: 1,
          }),
          discoveryActions.getSectionItems('trending', {
            type: 'all',
            page: 1,
            limit: 20,
            zenEnabled: true,
          }),
        ]);

        // Process categories
        if (categoriesRes.success && categoriesRes.data) {
          const apiCategories = categoriesRes.data.categories || [];
          setCategories([
            { key: 'all', label: 'All', slug: 'all' },
            { key: 'trending', label: 'Trending', slug: 'trending' },
            ...apiCategories.map((cat) => ({
              key: cat.slug || cat.category_id,
              label: cat.name,
              slug: cat.slug,
            })),
          ]);
        }

        // Process items
        if (itemsRes.success && itemsRes.data) {
          setItems((itemsRes.data.items || []).map(normalizeItem));
          setHasMore(itemsRes.data.pagination?.hasMore || false);
        }
      } catch (err) {
        console.error('Failed to fetch initial data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [isOpen]);

  // Fetch items when category changes (after initial load)
  useEffect(() => {
    // Skip if modal not open, still loading initial, or it's the default category
    if (!isOpen || loading || selectedCategory === 'all') return;

    const fetchCategoryItems = async () => {
      setLoading(true);
      setPage(1);

      try {
        const res = await discoveryActions.getSectionItems(selectedCategory, {
          type: 'all',
          page: 1,
          limit: 20,
          zenEnabled: true,
        });

        if (res.success && res.data) {
          setItems((res.data.items || []).map(normalizeItem));
          setHasMore(res.data.pagination?.hasMore || false);
        }
      } catch (err) {
        console.error('Failed to fetch category items:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    const nextPage = page + 1;

    try {
      const section = selectedCategory === 'all' ? 'trending' : selectedCategory;

      const res = await discoveryActions.getSectionItems(section, {
        type: 'all',
        page: nextPage,
        limit: 20,
        zenEnabled: true,
      });

      if (res.success && res.data) {
        const newItems = (res.data.items || []).map(normalizeItem);
        setItems((prev) => [...prev, ...newItems]);
        setPage(nextPage);
        setHasMore(res.data.pagination?.hasMore || false);
      }
    } catch (err) {
      console.error('Failed to load more items:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, page, selectedCategory]);

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollHeight - scrollTop - clientHeight < 300 && !loadingMore && hasMore) {
        loadMore();
      }
    },
    [loadMore, loadingMore, hasMore]
  );

  if (!isOpen) return null;

  // Desktop: modal with backdrop, Mobile: full screen
  if (!isMobile) {
    return (
      <div style={styles.desktopOverlay} onClick={onClose}>
        <div style={styles.desktopModal} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={styles.desktopHeader}>
            <h1 style={styles.title}>Create</h1>
            <div style={styles.searchContainer}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ flexShrink: 0 }}>
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search models & workflows..."
                style={styles.searchInput}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} style={styles.clearButton}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
            <button onClick={onClose} style={styles.closeButton}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Category Chips */}
          <div className="hide-scrollbar" style={styles.desktopCategoryContainer}>
            {categories.map((cat) => {
              const isActive = selectedCategory === cat.key;
              return (
                <button
                  key={cat.key}
                  onClick={() => setSelectedCategory(cat.key)}
                  style={{
                    ...styles.categoryChip,
                    background: isActive ? '#fff' : 'transparent',
                    border: isActive ? 'none' : `1px solid ${colors.border.primary}`,
                    color: isActive ? '#000' : '#fff',
                  }}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>

          {/* Scrollable Grid Content */}
          <div onScroll={handleScroll} style={styles.desktopContent}>
            {(loading || searching) && (
              <div style={styles.loadingContainer}>
                <div style={styles.loadingText}>{searching ? 'Searching...' : 'Loading...'}</div>
              </div>
            )}

            {!loading && !searching && displayItems.length > 0 && (
              <>
                <div style={styles.desktopGrid}>
                  {displayItems.map((item, idx) => (
                    <ModelItem
                      key={`${item.item_type}-${item.model_id || idx}`}
                      item={item}
                      size="medium"
                      onClick={() => onSelectItem?.(item)}
                    />
                  ))}
                </div>

                {!isSearchMode && loadingMore && (
                  <div style={styles.loadingMoreContainer}>
                    <div style={styles.loadingText}>Loading more...</div>
                  </div>
                )}
              </>
            )}

            {!loading && !searching && displayItems.length === 0 && debouncedSearch && (
              <div style={styles.emptyState}>
                <span style={{ fontSize: 32, marginBottom: 12 }}>🔍</span>
                <span style={{ color: colors.text.secondary, fontSize: 14 }}>No results for "{debouncedSearch}"</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Mobile: full screen layout
  return (
    <div style={styles.overlay}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Create</h1>
        <button onClick={onClose} style={styles.closeButton}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Search Input - Mobile */}
      <div style={styles.mobileSearchContainer}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ flexShrink: 0 }}>
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search models & workflows..."
          style={styles.searchInput}
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} style={styles.clearButton}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      {/* Category Chips */}
      <div className="hide-scrollbar" style={styles.categoryContainer}>
        {categories.map((cat) => {
          const isActive = selectedCategory === cat.key;
          return (
            <button
              key={cat.key}
              onClick={() => setSelectedCategory(cat.key)}
              style={{
                ...styles.categoryChip,
                background: isActive ? '#fff' : 'transparent',
                border: isActive ? 'none' : `1px solid ${colors.border.primary}`,
                color: isActive ? '#000' : '#fff',
              }}
            >
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Scrollable Content */}
      <div onScroll={handleScroll} style={styles.content}>
        {(loading || searching) && !displayItems.length && (
          <div style={styles.loadingContainer}>
            <div style={styles.loadingText}>{searching ? 'Searching...' : 'Loading...'}</div>
          </div>
        )}

        {!loading && !searching && displayItems.length > 0 && (
          <>
            <ModelItem 
              item={displayItems[0]} 
              size="featured" 
              onClick={() => onSelectItem?.(displayItems[0])} 
            />

            <div style={styles.grid}>
              {displayItems.slice(1).map((item, idx) => (
                <ModelItem
                  key={`${item.item_type}-${item.model_id || idx}`}
                  item={item}
                  size="medium"
                  onClick={() => onSelectItem?.(item)}
                />
              ))}
            </div>

            {!isSearchMode && loadingMore && (
              <div style={styles.loadingMoreContainer}>
                <div style={styles.loadingText}>Loading more...</div>
              </div>
            )}
          </>
        )}

        {!loading && !searching && displayItems.length === 0 && debouncedSearch && (
          <div style={styles.emptyState}>
            <span style={{ fontSize: 32, marginBottom: 12 }}>🔍</span>
            <span style={{ color: colors.text.secondary, fontSize: 14 }}>No results for "{debouncedSearch}"</span>
          </div>
        )}
      </div>
    </div>
  );
}


const styles = {
  // Mobile: full screen overlay
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: colors.background.primary,
    zIndex: zIndex.modal,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${spacing.md}px ${spacing.lg}px`,
    paddingTop: `calc(env(safe-area-inset-top, ${spacing.md}px) + ${spacing.md}px)`,
  },
  title: {
    fontSize: typography.fontSize.xxxl - 4,
    fontWeight: typography.fontWeight.bold,
    color: colors.text.primary,
    margin: 0,
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: radius.full,
    background: colors.surface.primary,
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  categoryContainer: {
    display: 'flex',
    gap: spacing.xs,
    overflowX: 'auto',
    padding: `0 ${spacing.lg}px ${spacing.md}px`,
    flexShrink: 0,
  },
  categoryChip: {
    padding: `${spacing.xs}px ${spacing.md}px`,
    borderRadius: radius.xl,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    transition: transitions.normal,
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: `0 ${spacing.lg}px`,
    paddingBottom: `calc(env(safe-area-inset-bottom, ${spacing.md}px) + ${spacing.md}px)`,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: 40,
  },
  loadingText: {
    color: colors.text.tertiary,
    fontSize: typography.fontSize.md,
  },
  loadingMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: spacing.lg,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  // Desktop: centered modal with backdrop
  desktopOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(8px)',
    zIndex: zIndex.modal,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xl,
  },
  desktopModal: {
    width: '100%',
    maxWidth: 900,
    maxHeight: '85vh',
    background: colors.background.primary,
    borderRadius: radius.lg,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: `1px solid ${colors.border.primary}`,
  },
  desktopHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${spacing.lg}px ${spacing.xl}px`,
    borderBottom: `1px solid ${colors.border.primary}`,
    flexShrink: 0,
  },
  desktopCategoryContainer: {
    display: 'flex',
    gap: spacing.xs,
    overflowX: 'auto',
    padding: `${spacing.md}px ${spacing.xl}px`,
    borderBottom: `1px solid ${colors.border.primary}`,
    flexShrink: 0,
  },
  desktopContent: {
    flex: 1,
    overflowY: 'auto',
    padding: spacing.xl,
  },
  desktopGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: spacing.md,
  },
  // Search styles
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: colors.surface.primary,
    border: `1px solid ${colors.border.primary}`,
    borderRadius: radius.md,
    padding: '8px 12px',
    flex: 1,
    maxWidth: 300,
    marginRight: spacing.md,
  },
  mobileSearchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: colors.surface.primary,
    border: `1px solid ${colors.border.primary}`,
    borderRadius: radius.md,
    padding: '10px 12px',
    margin: `0 ${spacing.lg}px ${spacing.sm}px`,
  },
  searchInput: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: colors.text.primary,
    fontSize: typography.fontSize.md,
    '::placeholder': {
      color: colors.text.tertiary,
    },
  },
  clearButton: {
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
};
